import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentRef } from 'react';

import {
  Box,
  Image as BricksImage,
  Col,
  Icons,
  PressableBox,
  Row,
  Text,
  useMedia,
} from '@opendoor/bricks-next';
import { globalObservability } from '@opendoor/observability/slim';
import Router from 'next/router';
import { Marker, Popup } from 'react-map-gl';

import type { MapMarkerProps } from './types';

import styles from './MapMarker.module.css';

function formatNumber(number: number) {
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: number >= 1_000_000 ? 2 : 0,
    notation: 'compact',
    compactDisplay: 'short',
  });
}

const PRIMARY_PHOTO_WIDTH = 350;
const PRIMARY_PHOTO_HEIGHT = 150;
const SECONDARY_PHOTO_WIDTH = 172;
const SECONDARY_PHOTO_HEIGHT = 100;

const PRIMARY_PHOTO_PARAMS = new URLSearchParams({
  service: 'cosmos',
  preset: '3:2-sm',
}).toString();
const THUMBNAIL_PHOTO_PARAMS = new URLSearchParams({
  service: 'cosmos',
  preset: '3:2-xs',
}).toString();

const HOVER_INTENT_MS = 120;
// Favorited markers need extra room for the heart icon. Non-favorited markers
// render at the narrower width so anonymous users (who can never see a heart)
// and signed-in users with no favorite on this property do not carry unused
// reserved space. Late-arriving `propertyReactions` that flip isLiked=true
// will widen the marker then, which is acceptable for the rare favorite flip.
const MARKER_WIDTH_WITH_HEART = 76;
const MARKER_WIDTH_NO_HEART = 60;
const MARKER_HEIGHT = 28;

// Dedupe RUM impressions so panning does not fire a new `mapMarkerHover` every
// time a popup opens. The Set is cleared on SPA route changes so navigating
// between markets (/homes/a -> /homes/b) re-fires impressions for repeated
// addresses that appear in the new market's results.
const markerImpressionsFired = new Set<string>();
let routerListenerAttached = false;

function ensureRouterListener() {
  if (routerListenerAttached || typeof window === 'undefined') {
    return;
  }
  routerListenerAttached = true;
  Router.events.on('routeChangeStart', () => {
    markerImpressionsFired.clear();
  });
}

// Test-only reset. The dedupe Set persists for the SPA session, so unit tests
// that render MapMarker multiple times need a way to reset it without relying
// on salted address tokens that mask bugs in the dedupe contract itself.
export const __resetMarkerImpressionsForTest = () => {
  markerImpressionsFired.clear();
};

function trackMarkerImpression(addressToken: string) {
  if (!addressToken || markerImpressionsFired.has(addressToken)) {
    return;
  }
  ensureRouterListener();
  markerImpressionsFired.add(addressToken);
  try {
    globalObservability.getDatadogRumClient?.()?.addAction?.('marketplace.mapMarkerHover', {
      addressToken,
    });
  } catch {
    // analytics must never break the UI
  }
}

function buildPhotoSlots(photos: readonly string[] | null | undefined) {
  const [primary, secondary, tertiary] = photos ?? [];
  return {
    primary: primary ? `${primary}?${PRIMARY_PHOTO_PARAMS}` : null,
    secondary: secondary ? `${secondary}?${THUMBNAIL_PHOTO_PARAMS}` : null,
    tertiary: tertiary ? `${tertiary}?${THUMBNAIL_PHOTO_PARAMS}` : null,
  };
}

function buildMarkerAriaLabel(property: MapMarkerProps['property']) {
  const parts: string[] = [];
  if (property.listPrice != null) {
    parts.push(`Property listed at $${property.listPrice.toLocaleString()}`);
  } else {
    parts.push('Property');
  }
  const beds = property.beds != null ? `${property.beds} beds` : null;
  const baths = property.baths != null ? `${property.baths} baths` : null;
  const summary = [beds, baths].filter(Boolean).join(', ');
  if (summary) {
    parts.push(summary);
  }
  if (property.isLiked) {
    parts.push('favorited');
  }
  return parts.join(', ');
}

type PopupImageProps = {
  uri: string | null;
  width: number;
  height: number;
  alt: string;
  borderTopLeftRadius?: '$8x';
  borderTopRightRadius?: '$8x';
};

const PopupImage = ({
  uri,
  width,
  height,
  alt,
  borderTopLeftRadius,
  borderTopRightRadius,
}: PopupImageProps) => {
  const [errored, setErrored] = useState(false);
  const showPlaceholder = !uri || errored;

  if (showPlaceholder) {
    return (
      <Box
        width={width}
        height={height}
        backgroundColor="$backgroundSecondary"
        borderTopLeftRadius={borderTopLeftRadius}
        borderTopRightRadius={borderTopRightRadius}
      />
    );
  }

  return (
    <BricksImage
      alt={alt}
      source={{ uri, width, height }}
      onError={() => setErrored(true)}
      borderTopLeftRadius={borderTopLeftRadius}
      borderTopRightRadius={borderTopRightRadius}
    />
  );
};

type PopupBodyProps = {
  property: MapMarkerProps['property'];
};

// Not wrapped in `memo`: callers pass a fresh `property` object on every
// MapMarker render, so shallow-prop comparison would always miss. The popup
// body only mounts while `isActive && isDesktop`, so re-renders are cheap
// enough to skip the optimization rather than add a custom comparator.
const PopupBody = ({ property }: PopupBodyProps) => {
  const slots = buildPhotoSlots(property.photos);

  return (
    <Box className={styles.popupEnter}>
      <PopupImage
        uri={slots.primary}
        width={PRIMARY_PHOTO_WIDTH}
        height={PRIMARY_PHOTO_HEIGHT}
        alt="map-marker-primary-property-image"
        borderTopLeftRadius="$8x"
        borderTopRightRadius="$8x"
      />
      <Box flexDirection="row" justifyContent="space-between" mt={6}>
        <PopupImage
          uri={slots.secondary}
          width={SECONDARY_PHOTO_WIDTH}
          height={SECONDARY_PHOTO_HEIGHT}
          alt="map-marker-secondary-property-image"
        />
        <PopupImage
          uri={slots.tertiary}
          width={SECONDARY_PHOTO_WIDTH}
          height={SECONDARY_PHOTO_HEIGHT}
          alt="map-marker-tertiary-property-image"
        />
      </Box>
      <Col mt="$10x" mx="$10x" mb="$8x" gap="$3x">
        <Row alignItems="baseline" justifyContent="space-between">
          {property.listPrice != null ? (
            <Text typography="$labelXlarge" tag="p" color="$contentPrimary">
              {`$${property.listPrice.toLocaleString()}`}
            </Text>
          ) : null}
          {property.isLiked ? (
            <Icons.FavoritedFilled
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore bricks-next types size as 20|24, but 14 is valid at runtime
              size={14}
              color="$contentStateErrorDefault"
              alignSelf="center"
            />
          ) : null}
        </Row>
        {(() => {
          const stats = [
            property.beds != null ? `${property.beds} bds` : null,
            property.baths != null ? `${property.baths} ba` : null,
            property.livingSquareFootage != null
              ? `${property.livingSquareFootage.toLocaleString()} sqft`
              : null,
          ].filter((part): part is string => part !== null);
          if (stats.length === 0) {
            return null;
          }
          return (
            <Row alignItems="baseline">
              {stats.map((part, index) => (
                <Fragment key={part}>
                  {index > 0 ? (
                    <Text tag="span" typography="$bodySmall" color="$contentStateInactiveSecondary">
                      {' • '}
                    </Text>
                  ) : null}
                  <Text tag="span" typography="$bodySmall" color="$contentSecondary">
                    {part}
                  </Text>
                </Fragment>
              ))}
            </Row>
          );
        })()}
        {property.street ? (
          <Text
            tag="p"
            typography="$bodySmall"
            color="$contentTertiary"
            $platform-web={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {property.street}
          </Text>
        ) : null}
      </Col>
    </Box>
  );
};

export const MapMarker = ({
  property,
  onClick,
  moveToTop,
  setFocused: focusTopMarker,
  isSelected,
  isLoading,
}: MapMarkerProps) => {
  const media = useMedia();
  const isDesktop = media.largerThanSM;
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isHovering || isFocused || Boolean(isSelected) || Boolean(isLoading);
  const pressableRef = useRef<HTMLElement | null>(null);
  // Stable ref callback so PressableBox isn't detached/re-attached every render.
  const setPressableRef = useCallback((node: ComponentRef<typeof PressableBox> | null) => {
    pressableRef.current = node as HTMLElement | null;
  }, []);
  const hoverTimerRef = useRef<number | null>(null);
  // Keyed by the preloaded href so the effect re-fires when the primary photo
  // URL changes. A simple boolean guard would block all subsequent preloads.
  const preloadedHrefRef = useRef<string | null>(null);
  // Mirror isFocused in a ref so the hover-intent timer callback always reads
  // the latest value instead of the closure captured at schedule time.
  const isFocusedRef = useRef(isFocused);
  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);
  const popupId = `cosmos-map-marker-popup-${property.addressToken}`;

  useEffect(() => {
    // Fire impressions regardless of viewport: focus / selection / loading can
    // all activate a marker on mobile (via tap-select) and those count as
    // engagement even when the desktop-only hover popup does not render.
    if (isActive) {
      trackMarkerImpression(property.addressToken);
    }
  }, [isActive, property.addressToken]);

  useEffect(() => {
    if (!isActive || !isDesktop) {
      return;
    }
    const primary = property.photos?.[0];
    if (!primary) {
      return;
    }
    const href = `${primary}?${PRIMARY_PHOTO_PARAMS}`;
    if (preloadedHrefRef.current === href) {
      return;
    }
    preloadedHrefRef.current = href;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
    return () => {
      link.parentNode?.removeChild(link);
    };
  }, [isActive, isDesktop, property.photos]);

  useEffect(() => {
    if (focusTopMarker) {
      pressableRef.current?.focus();
    }
  }, [focusTopMarker]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsFocused(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [isFocused]);

  const cancelHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => cancelHoverTimer();
  }, [cancelHoverTimer]);

  const handleOnHover = () => {
    cancelHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      setIsHovering(true);
      moveToTop?.(isFocusedRef.current);
    }, HOVER_INTENT_MS);
  };

  const handleOnMouseOut = () => {
    cancelHoverTimer();
    setIsHovering(false);
  };

  const handleOnFocus = () => {
    moveToTop?.(true);
    setIsFocused(true);
  };

  const handleOnBlur = () => {
    setIsFocused(false);
  };

  const handleOnClick = () => {
    cancelHoverTimer();
    setIsHovering(false);
    setIsFocused(false);
    onClick();
  };

  const ariaLabel = buildMarkerAriaLabel(property);

  return (
    <Marker
      key={`map-marker-${property.addressToken}`}
      latitude={property.location?.lat}
      longitude={property.location?.lon}
    >
      <PressableBox
        aria-label={ariaLabel}
        aria-expanded={isActive && isDesktop}
        aria-describedby={isActive && isDesktop ? popupId : undefined}
        analyticsName="property-marker-click"
        backgroundColor={isActive ? '$backgroundInversePrimary' : '$backgroundPrimary'}
        borderRadius="$32x"
        height={MARKER_HEIGHT}
        width={property.isLiked ? MARKER_WIDTH_WITH_HEART : MARKER_WIDTH_NO_HEART}
        zIndex={1}
        tabIndex={0}
        justifyContent="center"
        alignItems="center"
        onMouseEnter={handleOnHover}
        onMouseLeave={handleOnMouseOut}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onPress={handleOnClick}
        ref={setPressableRef}
        data-testid={`map-marker-${property.addressToken}`}
      >
        <Box flexDirection="row" alignItems="center" justifyContent="center">
          {isLoading ? (
            <Text tag="span" typography="$labelSmall" color="$contentInversePrimary">
              •••
            </Text>
          ) : (
            <>
              {property.isLiked ? (
                <Icons.FavoritedFilled
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore bricks-next types size as 20|24, but 14 is valid at runtime
                  size={14}
                  color="$contentStateErrorDefault"
                  pr={4}
                />
              ) : null}
              {property.listPrice != null ? (
                <Text
                  tag="span"
                  typography="$labelSmall"
                  color={isActive ? '$contentInversePrimary' : '$contentPrimary'}
                >
                  {formatNumber(property.listPrice)}
                </Text>
              ) : null}
            </>
          )}
        </Box>
      </PressableBox>
      {isActive && isDesktop ? (
        <Popup
          longitude={property.location?.lon}
          latitude={property.location?.lat}
          anchor="bottom"
          offset={20}
          closeButton={false}
        >
          <Box id={popupId} role="region">
            <PopupBody property={property} />
          </Box>
        </Popup>
      ) : null}
    </Marker>
  );
};
